import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

type OpenHistoryFn = () => void;

type FileRecordHistoryTriggerValue = {
  /**
   * Registers an "open history" callback owned by a subtree that can't be
   * reached from the pane header directly — e.g. the Notion-like editor, whose
   * version history lives in its own tiptap snapshot context.
   */
  registerOpenHistory: (fn: OpenHistoryFn | null) => void;
  /** Opens whatever history was registered (no-op if nothing is registered). */
  openHistory: OpenHistoryFn;
  /** True while a history trigger is registered (editor mounted + ready). */
  isRegistered: boolean;
};

const FileRecordHistoryTriggerContext =
  createContext<FileRecordHistoryTriggerValue | null>(null);

/**
 * Bridges an in-editor history toggle out to the pane header kebab. The editor
 * registers its toggle; the kebab menu item invokes it. Lets version history be
 * a single manifest menu item regardless of which subsystem owns the history.
 */
export function FileRecordHistoryTriggerProvider({
  children,
}: {
  children: ReactNode;
}) {
  const openRef = useRef<OpenHistoryFn | null>(null);
  const [isRegistered, setIsRegistered] = useState(false);

  const registerOpenHistory = useCallback((fn: OpenHistoryFn | null) => {
    openRef.current = fn;
    setIsRegistered(!!fn);
  }, []);

  const openHistory = useCallback(() => {
    openRef.current?.();
  }, []);

  const value = useMemo(
    () => ({ registerOpenHistory, openHistory, isRegistered }),
    [registerOpenHistory, openHistory, isRegistered],
  );

  return (
    <FileRecordHistoryTriggerContext.Provider value={value}>
      {children}
    </FileRecordHistoryTriggerContext.Provider>
  );
}

export function useFileRecordHistoryTrigger() {
  return useContext(FileRecordHistoryTriggerContext);
}
